import {API_ENDPOINTS} from '../constants';
import {getAuthToken, IOrganization, IAccount} from './account';

export interface IPage {
    status: 'reviewing' | 'draft' | 'reviewed' | 'rejected';
    accountName: string;
    partnerName: string;
    direction: 'outgoing' | 'incoming';
    dbOwnerName: string;
    creatorName: string;
    approved: boolean;
    created: number;
    updated: number;
    databaseName?: string;
    databaseTitle?: string;
    database?: {
        name: string;
        title: string;
    };
    organization:
        | string
        | {
              name: string;
              title: string;
          };
    name: string;
    title: string;
    type: string;
    row: {
        action: 'add';
        name: null;
        time: number;
        map: any;
    };
}

export async function save(
    title: string,
    accountName: string,
    databaseName: string,
    row: {map: object},
    pageName?: string
): Promise<{success: boolean; data: IPage}> {
    const req = await fetch(API_ENDPOINTS.pages(), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            title,
            accountName,
            databaseName,
            row,
            ...(pageName ? {name: pageName} : {}),
        }),
    });
    return {
        success: req.ok,
        data: await req.json(),
    };
}

export async function approve(name: string): Promise<{success: boolean}> {
    const req = await fetch(API_ENDPOINTS.approvePage(name), {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    return {
        success: req.ok,
    };
}

export async function unapprove(name: string): Promise<{success: boolean}> {
    const req = await fetch(API_ENDPOINTS.unapprovePage(name), {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    return {
        success: req.ok,
    };
}

export async function submit(name: string): Promise<{success: boolean}> {
    const req = await fetch(`${API_ENDPOINTS.pages()}/${name}/review`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });
    return {
        success: req.ok,
    };
}

export async function deletePage(name: string): Promise<{success: boolean}> {
    const req = await fetch(`${API_ENDPOINTS.pages()}/${name}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({}), // body required for modsec
    });
    return {
        success: req.ok,
    };
}

export async function reject(name: string): Promise<{success: boolean}> {
    const req = await fetch(`${API_ENDPOINTS.pages()}/${name}/reject`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({}), // body required for modsec
    });
    return {
        success: req.ok,
    };
}

export async function get(
    id?: string,
    orgName?: string
): Promise<{success: boolean; pages: IPage[] | null}> {
    const result: {success: boolean; pages: IPage[] | null} = {
        success: false,
        pages: null,
    };
    // TODO use endpoint function
    const endpoint = `${API_ENDPOINTS.pages()}${orgName ? `/${orgName}` : ''}${id ? `/${id}` : ''}`;
    try {
        const request = await fetch(endpoint, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
        });
        if (request.ok) {
            const page: IPage | IPage[] = await request.json();
            result.success = true;
            if (Array.isArray(page)) result.pages = page;
            else result.pages = [page];
        }
    } catch (ex) {
        console.error(ex);
        result.success = false;
        alert('There was an error fetching your pages. Please reload and try again');
    }
    return result;
}

export async function getAll(): Promise<{success: boolean; pages: IPage[] | null}> {
    const result: {success: boolean; pages: IPage[] | null} = {
        success: false,
        pages: null,
    };
    // TODO use endpoint function
    try {
        const pagesRequest = await fetch(API_ENDPOINTS.pages(), {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
        });
        // const pending = await fetch(API_ENDPOINTS.pendingPages(), {
        //     method: 'GET',
        //     headers: {
        //         Authorization: `Bearer ${getAuthToken()}`,
        //         'Content-Type': 'application/json',
        //     },
        // });
        if (pagesRequest.ok) {
            const pages: IPage[] = await pagesRequest.json();
            result.success = true;
            result.pages = [];
            if (Array.isArray(pages)) result.pages = [...pages];
        }
    } catch (ex) {
        console.error(ex);
        result.success = false;
        alert('There was an error fetching your pages. Please reload and try again');
    }
    return result;
}

/**
 * Fetches the publicly available organization info.
 * For use with associated information pages in public or embedded display.
 *
 * @param orgName {string}
 */
export async function getOrganization(
    orgName: string
): Promise<{success: boolean; organization: IOrganization | null}> {
    const result: {success: boolean; organization: IOrganization | null} = {
        success: false,
        organization: null,
    };
    const endpoint = API_ENDPOINTS.public.organization(orgName);
    try {
        const request = await fetch(endpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (request.ok) {
            const org: IOrganization = await request.json();
            result.success = true;
            result.organization = org;
        }
    } catch (ex) {
        console.error(ex);
    }
    return result;
}

/**
 * Fetches the publicly available organization info.
 * For use with associated information pages in public or embedded display.
 *
 * @param orgName {string}
 */
export async function getOwner(
    orgName: string
): Promise<{success: boolean; owner: IAccount | null}> {
    const result: {success: boolean; owner: IAccount | null} = {
        success: false,
        owner: null,
    };
    const endpoint = API_ENDPOINTS.public.owner(orgName);
    try {
        const request = await fetch(endpoint, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
        });
        if (request.ok) {
            const owner = await request.json();
            result.success = true;
            result.owner = owner;
        }
    } catch (ex) {
        console.error(ex);
    }
    return result;
}

export async function getPending(): Promise<{success: boolean; pages: IPage[] | null}> {
    const result: {success: boolean; pages: IPage[] | null} = {
        success: false,
        pages: null,
    };
    const endpoint = API_ENDPOINTS.pendingPages();
    try {
        const request = await fetch(endpoint, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
        });
        if (request.ok) {
            const page: IPage | IPage[] = await request.json();
            result.success = true;
            if (Array.isArray(page)) result.pages = page;
            else result.pages = [page];
        }
    } catch (ex) {
        console.error(ex);
        result.success = false;
    }
    return result;
}

export async function getLoactionHash(
    payload: Address
): Promise<{success: boolean; data: AddressHash | null}> {
    const result: {success: boolean; data: AddressHash | null} = {
        success: false,
        data: null,
    };
    const endpoint = API_ENDPOINTS.geoHash();
    try {
        const request = await fetch(endpoint, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${getAuthToken()}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        if (request.ok) {
            const data: AddressHash = await request.json();
            result.success = true;
            result.data = data;
        }
    } catch (ex) {
        console.error(ex);
        result.success = false;
    }
    return result;
}



// WEBPACK FOOTER //
// ./src/services/pages.ts